/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Spin,
  Col,
} from 'antd';
import Table from 'components/Table';
import currencyFormatter from 'utils/currencyFormatter';
import CurrencyInput from 'components/CurrencyInput';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { checkToken } from 'api/utils';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import percentFormatter from 'utils/percentFormatter';

const NominasISR = ({ data, encabezado }) => {
  const [loading, setLoading] = useState(false);
  const [percepciones, setPercepciones] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _percepciones,
      ] = await Promise.all([
        getPercepciones(),
      ]);
      if (mounted) {
        setPercepciones(_percepciones);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  // Descuento de Incidencia aplicado solo para efectos de ISR (ej. Falta Injustificada con
  // "Se Descuenta del Sueldo para ISR"): el motor NO reduce el importe pagado de la
  // percepción (se sigue pagando completo), solo la base usada para Exento/Gravado -- ver
  // calcular_DescuentoIncidenciaPercepcion() en nominas_calculos.py. Se deriva aquí en vez de
  // persistirse en un campo aparte, por lo que Importe - Descuento ISR = Exento + Gravado.
  const descuentoISR = (record) => {
    const importe = parseFloat(record?.importe) || 0;
    const excento = parseFloat(record?.importe_isr_excento) || 0;
    const gravado = parseFloat(record?.importe_isr_gravado) || 0;
    return Math.max(0, importe - (excento + gravado));
  };

  const totalImporte = data?.reduce((acc, curr) => acc
    + (parseFloat(curr.importe) || 0), 0) || 0;
  const totalExento = data?.reduce((acc, curr) => acc + (
    parseFloat(curr.importe_isr_excento) || 0), 0) || 0;
  const totalGravado = data?.reduce((acc, curr) => acc
    + (parseFloat(curr.importe_isr_gravado) || 0), 0) || 0;
  const totalDescuentoISR = data?.reduce((acc, curr) => acc + descuentoISR(curr), 0) || 0;

  const totalGeneral = totalGravado;

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 200,
      render: (val) => {
        const _percepcion = percepciones.find((e) => e.id === val);
        return `${_percepcion?.clave} - ${_percepcion?.descripcion}`;
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Descuento para ISR',
      dataIndex: 'descuento_isr',
      key: 'descuento_isr',
      width: 200,
      align: 'right',
      render: (_val, record) => currencyFormatter(descuentoISR(record)),
    },
    {
      titleText: 'Exento',
      dataIndex: 'importe_isr_excento',
      key: 'importe_isr_excento',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Gravado',
      dataIndex: 'importe_isr_gravado',
      key: 'importe_isr_gravado',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  const columnsSubsidios = [
    {
      titleText: 'Base Gravable',
      dataIndex: 'importe_isr_base_gravable',
      key: 'importe_isr_base_gravable',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Base Gravable Anterior',
      dataIndex: 'importe_isr_base_gravable_anterior',
      key: 'importe_isr_base_gravable_anterior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Base Gravable Probable por Causar',
      dataIndex: 'importe_isr_base_gravable_por_causar',
      key: 'importe_isr_base_gravable_por_causar',
      width: 270,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Total Base Gravable',
      dataIndex: 'importe_isr_total_base_gravable',
      key: 'importe_isr_total_base_gravable',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Límite Inferior',
      dataIndex: 'importe_isr_limite_inferior',
      key: 'importe_isr_limite_inferior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Excendente',
      dataIndex: 'importe_isr_excedente',
      key: 'importe_isr_excedente',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: '% de Excedente',
      dataIndex: 'importe_isr_percent_excedente',
      key: 'importe_isr_percent_excedente',
      width: 200,
      align: 'right',
      render: (val) => percentFormatter(val || 0),
    },
    {
      titleText: 'Impuesto Marginal',
      dataIndex: 'importe_isr_impuesto_marginal',
      key: 'importe_isr_impuesto_marginal',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Cuota Fija',
      dataIndex: 'importe_isr_cuota_fija',
      key: 'importe_isr_cuota_fija',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR Causado Total',
      dataIndex: 'importe_isr_causado_total',
      key: 'importe_isr_causado_total',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Subsidio Aplicado',
      dataIndex: 'importe_subsidio_causado_total',
      key: 'importe_subsidio_causado_total',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR Causado Previo',
      dataIndex: 'importe_isr_causado_previo',
      key: 'importe_isr_causado_previo',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR Retenido Anterior',
      dataIndex: 'importe_isr_retenido_anterior',
      key: 'importe_isr_retenido_anterior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR Por Retener',
      dataIndex: 'importe_isr_por_retener',
      key: 'importe_isr_por_retener',
      width: 250,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR Subtotal',
      dataIndex: 'importe_isr_subtotal',
      key: 'importe_isr_subtotal',
      width: 250,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Compensación ISR',
      dataIndex: 'importe_isr_compensacion',
      key: 'importe_isr_compensacion',
      width: 250,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR por Retener de Ésta Nómina',
      dataIndex: 'importe_isr_retenido',
      key: 'importe_isr_retenido',
      width: 250,
      align: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  const base_function = (field) => (suma, actual) => suma + (actual?.[field] || 0);

  const summaryCells = [
    {
      value: 'Total',
      valueAlign: 'left',
      render: (val) => <strong>{val}</strong>,
    },
    {
      value: data?.reduce(base_function('importe'), 0),
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      value: totalDescuentoISR,
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      value: data?.reduce(base_function('importe_isr_excento'), 0),
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      value: data?.reduce(base_function('importe_isr_gravado'), 0),
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  return (
    <Row className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          <Table
            cols={columns}
            data={data}
            allowExportPDF={false}
            summaryCells={summaryCells}
          />
        </Spin>
      </Col>
      <Col span={24}>
        <Table
          cols={columnsSubsidios}
          data={[encabezado]}
          allowExportPDF={false}
        />
      </Col>
    </Row>
  );
};

export default NominasISR;
